import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import AppImage from '../AppImage';

const StaticMapModal = ({ isOpen, onClose, mapUrl, trailName, children }) => {
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  if (!isOpen) return null;

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const handleOverlayClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      onClose();
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[9999]"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="map-modal-title"
    >
      <div className="bg-card rounded-lg shadow-2xl max-w-5xl max-h-[90vh] w-full overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <h2 id="map-modal-title" className="font-heading font-semibold text-lg text-foreground">
              Official Trail Map
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {trailName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-muted"
            aria-label="Close map modal"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Modal Content */}
        <div className="p-4 max-h-[calc(90vh-80px)] overflow-auto">
          {children}
          
          {/* Map Image */}
          <div className="relative bg-muted rounded-lg overflow-hidden">
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center min-h-[400px]">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin">
                    <Icon name="Loader2" size={32} className="text-primary" />
                  </div>
                  <p className="text-muted-foreground">Loading trail map...</p>
                </div>
              </div>
            )}
            
            {imageError ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
                <Icon name="ImageOff" size={48} className="text-muted-foreground mb-4" />
                <h3 className="font-heading font-medium text-lg text-foreground mb-2">
                  Map Not Available
                </h3>
                <p className="text-muted-foreground text-center">
                  The trail map could not be loaded at this time. Please try again later.
                </p>
              </div>
            ) : (
              <AppImage
                src={mapUrl}
                alt={`Official trail map for ${trailName} showing the complete route with waypoints and landmarks`}
                className="w-full h-auto object-contain"
                onLoad={handleImageLoad}
                onError={handleImageError}
                style={{ 
                  display: imageLoading ? 'none' : 'block',
                  maxHeight: 'calc(90vh - 200px)'
                }}
              />
            )}
          </div>

          {/* Map Instructions */}
          <div className="mt-4 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon name="Info" size={14} className="text-primary-foreground" />
              </div>
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">How to use this map:</p>
                <ul className="space-y-1">
                  <li>• Click and drag to pan around the map</li>
                  <li>• Use your browser's zoom controls to see details</li>
                  <li>• Follow the marked trail route and waypoints</li>
                  <li>• Print or save this map for offline reference</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/20">
          <div className="text-xs text-muted-foreground">
            Press <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Esc</kbd> or click outside to close
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(mapUrl, '_blank')}
              iconName="ExternalLink"
              iconPosition="left"
            >
              Open Full Size
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticMapModal;